import { isAxiosError } from "axios";
import {
  AssetWithPosition,
  ReportSection,
  PortfolioStatementRequest,
  PortfolioStatementResponse,
  PortfolioStatementStatusResponse,
} from "../types";
import { assetDetailsGw as gw } from "./axios/assetDetailsGw";
import { getBrowserTimezone } from "../../utils/dateUtils";

const REPORT_SECTIONS: ReportSection[] = [
  {
    id: "transaction_history",
    label: "Transaction History",
    translationKey: "portfolio-statement.section.transaction-history",
    defaultChecked: true,
  },
  {
    id: "payout_history",
    label: "Payout History",
    translationKey: "portfolio-statement.section.payout-history",
    defaultChecked: true,
  },
  {
    id: "investment_overview",
    label: "Investment Overview",
    translationKey: "portfolio-statement.section.investment-overview",
    defaultChecked: false,
  },
  {
    id: "performance_overview",
    label: "Performance Overview",
    translationKey: "portfolio-statement.section.performance-overview",
    defaultChecked: false,
  },
  {
    id: "custody_overview",
    label: "Custody Overview",
    translationKey: "portfolio-statement.section.custody-overview",
    defaultChecked: false,
  },
];

/** Maximum number of polling attempts for async statement generation. */
const MAX_POLL_ATTEMPTS = 30;

/** Delay in ms between polling attempts. */
const POLL_INTERVAL_MS = 2000;

export namespace PortfolioStatementClient {
  export const getInvestorAssets = async (
    signal?: AbortSignal,
  ): Promise<AssetWithPosition[]> => {
    try {
      const { data } = await gw.get<{ data: AssetWithPosition[] }>(
        "/portfolio/assets",
        { signal },
      );
      return data.data;
    } catch (error) {
      const status = isAxiosError(error) ? error.response?.status : undefined;
      if (status === 404) return [];
      console.error("[PortfolioStatement] Failed to fetch assets:", error);
      throw error;
    }
  };

  export const getReportSections = (): Promise<ReportSection[]> => {
    return Promise.resolve(REPORT_SECTIONS);
  };

  export const generateStatement = async (
    request: PortfolioStatementRequest,
    signal?: AbortSignal,
  ): Promise<PortfolioStatementResponse> => {
    try {
      const response = await gw.post<PortfolioStatementResponse>(
        "/portfolio/statement/generate",
        // Resolved here rather than at every call site, so any consumer gets
        // local times. Spread last so an explicit timezone still wins.
        { timezone: getBrowserTimezone(), ...request },
        { signal },
      );

      // Synchronous generation - PDF URL is immediately available
      if (response.status === 200 && response.data.url) {
        return response.data;
      }

      // Asynchronous generation - need to poll for status
      if (response.status === 202 && response.data.statementId) {
        return await pollStatementStatus(response.data.statementId, signal);
      }

      return response.data;
    } catch (error) {
      console.error(
        "[PortfolioStatement] Failed to generate statement:",
        error,
      );
      throw error;
    }
  };

  export const getStatementStatus = async (
    statementId: string,
    signal?: AbortSignal,
  ): Promise<PortfolioStatementStatusResponse> => {
    const { data } = await gw.get<PortfolioStatementStatusResponse>(
      `/portfolio/statement/${statementId}/status`,
      { signal },
    );
    return data;
  };

  const pollStatementStatus = async (
    statementId: string,
    signal?: AbortSignal,
  ): Promise<PortfolioStatementResponse> => {
    for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));

      if (signal?.aborted) {
        throw new DOMException("Aborted", "AbortError");
      }

      const statusResponse = await getStatementStatus(statementId, signal);

      if (statusResponse.status === "completed" && statusResponse.url) {
        return {
          url: statusResponse.url,
          statementId: statusResponse.statementId,
          expiresAt: statusResponse.expiresAt,
        };
      }

      if (statusResponse.status === "failed") {
        throw new Error("Statement generation failed");
      }
    }

    throw new Error("Statement generation timed out");
  };

  /**
   * Extracts the Retry-After value from an Axios error response.
   * Returns the number of seconds to wait, or undefined if not present.
   */
  export const getRetryAfterSeconds = (error: unknown): number | undefined => {
    if (!isAxiosError(error)) return undefined;
    const retryAfter = error.response?.headers?.["retry-after"] as
      | string
      | undefined;
    if (!retryAfter) return undefined;
    const seconds = Number(retryAfter);
    return isNaN(seconds) ? undefined : seconds;
  };
}
